import React, { Component } from 'react'
import _ from 'lodash'
import { connect } from 'react-redux'
import { openLoader, closeLoader } from '../../../../../../redux-store/loader'
import { closeModal, openModal } from '../../../../../../redux-store/modal'
import PaymentModel from '../../../../../../data/models/payment/payment'
import I18n from '../../../../../../i18n'
import { Segment, Header, Table, AccordionTitle, Icon, AccordionContent, Label, Popup } from 'semantic-ui-react'
import moment from 'moment'
import ExtendProLicenses from './ExtendProLicenses'
import ExtendPremiumLicenses from './ExtendPremiumLicenses'
import ProLicenseStatus from './ProLicenseStatus'

const STATUS_COLORS = {
    live: 'green',
    scheduled: 'blue',
    expired: 'grey'
};

/**
 * Apartado de licencias de un profesor (o de un usuario premium).
 *
 * El orden es deliberado: primero el estado actual, después el ajuste y al final el
 * historial. Antes el historial abría el apartado y se leía como si fuese un
 * conjunto de reglas del que el sistema elegía una; en realidad es el registro de lo
 * que ha ido pasando, y solo una de sus filas está en vigor.
 */
class ProLicensesAccordion extends Component {

    constructor(props) {
        super(props)

        this.state = {
            licenses: [],
            overview: null,
            column: 'startDate',
            direction: 'descending',
            originalData: []
        }
    }

    componentDidMount() {

        this.getLicenses();

    }

    getLicenses = async () => {

        const message = {
            headerTitle: "Error",
            title: I18n.t('messages.modal.commonError'),
            buttons: [
                {
                    text: I18n.t('actions.accept'),
                    callback: () => this.props.closeModal()
                }
            ]
        };

        try {

            this.props.openLoader();

            if (this.props.type === 'pro') {

                // El overview trae el historial ya anotado (qué fila manda hoy y
                // cuál caducó) y la fecha de cobro de Stripe, que es justo lo que no
                // se podía contrastar desde aquí.
                const response = await PaymentModel.getProLicenseOverview(this.props.userid);
                const overview = response.data || {};
                const licenses = overview.licenses || [];

                this.setState({
                    overview,
                    licenses: _.orderBy(licenses, ['startDate'], ['desc']),
                    originalData: licenses
                });

            } else {

                const response = await PaymentModel.getUserLicenses(this.props.userid);

                if (response.data) {

                    this.setState({
                        licenses: _.orderBy(response.data, ['startDate'], ['desc']),
                        originalData: response.data
                    });

                }

            }

        } catch (error) {

            this.props.closeLoader();
            this.props.openModal(message);

        } finally {

            this.props.closeLoader();

        }

    }

    handleSort = (clickedColumn) => () => {

        const { column, direction, originalData } = this.state;

        if (column !== clickedColumn) {

            this.setState({
                column: clickedColumn,
                licenses: _.orderBy(originalData, [clickedColumn], ['asc']),
                direction: 'ascending',
            });

            return;

        }

        this.setState({
            licenses: _.orderBy(originalData, [clickedColumn], [direction === 'ascending' ? 'desc' : 'asc']),
            direction: direction === 'ascending' ? 'descending' : 'ascending',
        });

    }

    renderHistoryRow = license => {

        const isPro = this.props.type === 'pro';

        return (
            <Table.Row key={ license._id } positive={ !!license.current }>
                <Table.Cell>{ moment(license.startDate).format('DD/MM/YYYY') }</Table.Cell>
                <Table.Cell>{ moment(license.endDate).format('DD/MM/YYYY') }</Table.Cell>
                {
                    isPro && (
                        <React.Fragment>
                            <Table.Cell>
                                { license.name || '—' }
                                { license.current && <Label size="mini" color="green" style={{ marginLeft: '0.5rem' }}>{ I18n.t('pro.info.history.current') }</Label> }
                            </Table.Cell>
                            <Table.Cell>{ license.maxStudents }</Table.Cell>
                            <Table.Cell>
                                <Label basic size="mini" color={ STATUS_COLORS[license.status] || 'grey' }>
                                    { I18n.t(`pro.info.history.status.${ license.status || 'expired' }`) }
                                </Label>
                            </Table.Cell>
                            <Table.Cell>
                                {
                                    license.note
                                        ? <Popup trigger={ <span>{ I18n.t(`pro.info.history.origin.${ license.origin || 'unknown' }`) }</span> } content={ license.note } />
                                        : I18n.t(`pro.info.history.origin.${ license.origin || 'unknown' }`)
                                }
                            </Table.Cell>
                            <Table.Cell>{ license.updatedAt ? moment(license.updatedAt).format('DD/MM/YYYY HH:mm') : '—' }</Table.Cell>
                        </React.Fragment>
                    )
                }
            </Table.Row>
        );

    }

    render() {

        const { licenses, column, direction, overview } = this.state;

        const { index, activeIndex, type, handleClick } = this.props;

        const isPro = type === 'pro';

        const editTitle = isPro ? I18n.t('pro.info.adjust.title') : I18n.t('user.info.extendLicensePremium');

        return (
            <React.Fragment>

                { isPro && <ProLicenseStatus overview={ overview } /> }

                <Segment>

                    <Header as="h3">{ editTitle }</Header>

                    <p>
                        <Icon name='warning circle' color='yellow' />
                        { isPro ? I18n.t('pro.info.warning') : I18n.t('user.info.extendNote') }
                    </p>

                    {
                        isPro
                            ? <ExtendProLicenses
                                { ...this.props }
                                currentEnd={ _.get(overview, 'coverageEnd') }
                                onApplied={ () => this.getLicenses() }
                                getProLicences={ () => this.getLicenses() } />
                            : <ExtendPremiumLicenses { ...this.props } getUserLicences={ () => this.getLicenses() } /> }

                </Segment>

                <Segment>

                    <AccordionTitle
                        active={ activeIndex === index }
                        index={ index }
                        onClick={ handleClick }
                    >
                        <Icon name='dropdown' />
                        { isPro ? I18n.t('pro.info.history.title') : I18n.t('user.info.licenseList') }
                    </AccordionTitle>

                    <AccordionContent active={ activeIndex === index }>

                        { isPro && <p className="proLicenseHistoryHint">{ I18n.t('pro.info.history.hint') }</p> }

                        <Table celled selectable sortable>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell onClick={ this.handleSort('startDate') } sorted={ column === 'startDate' ? direction : null }>{ I18n.t('user.info.startDate') }</Table.HeaderCell>
                                    <Table.HeaderCell onClick={ this.handleSort('endDate') } sorted={ column === 'endDate' ? direction : null }>{ I18n.t('user.info.endDate') }</Table.HeaderCell>
                                    {
                                        isPro && (
                                            <React.Fragment>
                                                <Table.HeaderCell onClick={ this.handleSort('name') } sorted={ column === 'name' ? direction : null }>{ I18n.t('user.info.name') }</Table.HeaderCell>
                                                <Table.HeaderCell>{ I18n.t('pro.info.maxStudents') }</Table.HeaderCell>
                                                <Table.HeaderCell onClick={ this.handleSort('status') } sorted={ column === 'status' ? direction : null }>{ I18n.t('user.info.status') }</Table.HeaderCell>
                                                <Table.HeaderCell onClick={ this.handleSort('origin') } sorted={ column === 'origin' ? direction : null }>{ I18n.t('pro.info.history.originLabel') }</Table.HeaderCell>
                                                <Table.HeaderCell onClick={ this.handleSort('updatedAt') } sorted={ column === 'updatedAt' ? direction : null }>{ I18n.t('pro.info.history.updatedAt') }</Table.HeaderCell>
                                            </React.Fragment>
                                        )
                                    }
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                { licenses.map(this.renderHistoryRow) }
                            </Table.Body>
                        </Table>

                    </AccordionContent>

                </Segment>

            </React.Fragment>
        )
    }
}

export default connect(null, { openLoader, closeLoader, closeModal, openModal })(ProLicensesAccordion);
